import {
  Command,
  Plugin,
  DocumentSelection,
  Element,
  ButtonView,
} from "ckeditor5";

import { IconResetTableLine } from "@examspark/common-ui/dist/components/icons";

import { getSelectionAffectedTable } from "../_nonpublic/table/common";
import { visitModel } from "../util/document";

export const COMMAND_RESET_TABLE_STYLES = "resetTableStyles";
export const UI_BUTTON_RESET_TABLE_STYLES = "resetTableStyles";

const TABLE_STYLE_ATTRIBUTES = [
  "tableAlignment",
  "tableBackgroundColor",
  "tableBorderColor",
  "tableBorderStyle",
  "tableBorderWidth",
  "tableClass",
  "tableHeight",
  "tableWidth",
];

const TABLE_CELL_STYLE_ATTRIBUTES = [
  "tableCellBackgroundColor",
  "tableCellBorderColor",
  "tableCellBorderStyle",
  "tableCellBorderWidth",
  "tableCellHeight",
  "tableCellHorizontalAlignment",
  "tableCellPadding",
  "tableCellVerticalAlignment",
  "tableCellWidth",
];

const getSelectionAffectedTableStyles = (selection: DocumentSelection) => {
  const table = getSelectionAffectedTable(selection);
  const results: Array<{ element: Element; attribute: string | false }> = [];
  if (table == null) return results;

  for (const attribute of TABLE_STYLE_ATTRIBUTES) {
    if (table.hasAttribute(attribute)) {
      results.push({ element: table, attribute });
    }
  }

  visitModel(table, (e, opts) => {
    if (!e.is("element")) return;

    switch (e.name) {
      case "tableCell":
        //
        // Don't mess with cells of nested tables
        //
        opts.stopDescending();

        for (const attribute of TABLE_CELL_STYLE_ATTRIBUTES) {
          if (e.hasAttribute(attribute)) {
            results.push({ element: e, attribute });
          }
        }
        break;
      case "tableColumnGroup":
        results.push({ element: e, attribute: false });
        break;
    }
  });

  return results;
};

export class ResetTableStylesCommand extends Command {
  override refresh() {
    const results = getSelectionAffectedTableStyles(
      this.editor.model.document.selection,
    );

    this.isEnabled = results.length > 0;
  }

  override execute() {
    const model = this.editor.model;
    const results = getSelectionAffectedTableStyles(model.document.selection);
    if (results.length === 0) return;

    model.change((writer) => {
      for (const result of results) {
        if (!result.attribute) {
          writer.remove(result.element);
        } else {
          writer.removeAttribute(result.attribute, result.element);
        }
      }
    });
  }
}

export default class ResetTableStyles extends Plugin {
  init() {
    const command = new ResetTableStylesCommand(this.editor);
    this.editor.commands.add(COMMAND_RESET_TABLE_STYLES, command);

    this.editor.ui.componentFactory.add(
      UI_BUTTON_RESET_TABLE_STYLES,
      (locale) => {
        const button = new ButtonView(locale);

        button.set({
          tooltip: this.editor.t("Reset table styles"),
          icon: IconResetTableLine.Content,
        });

        button.bind("isEnabled").to(command, "isEnabled");

        button.on("execute", () => command.execute());

        return button;
      },
    );
  }
}
